/**
 * Skeleton - themed wrapper around `react-loading-skeleton`.
 *
 * Establishes the project-wide rule: components do NOT hand-roll loading
 * placeholders out of `animate-pulse` divs. Instead, every loading state
 * renders the *same* DOM as the loaded state and substitutes the data-bound
 * spans for `<Skeleton>` primitives. Layout, padding, borders and typography
 * then share their source with the loaded state.
 *
 * The wrapper uses live, theme-aware card colour tokens. Use the
 * `SkeletonGroup` provider once at the top of a loading tree to scope the
 * theme; bare `<Skeleton>` calls inherit the defaults exported here.
 *
 * @module components/ui/Skeleton
 */

import React from 'react';
import RLSSkeleton, {
  SkeletonTheme,
  type SkeletonProps as RLSSkeletonProps,
} from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

/** CSS variables follow theme changes without a React rerender or layout read. */
const SkeletonPalette = React.createContext({
  baseColor: 'var(--skeleton-base, #2A2A30)',
  highlightColor: 'var(--skeleton-highlight, #34343B)',
});

/** Props for the themed `<Skeleton>`. Re-exports the lib's prop shape. */
export type SkeletonProps = RLSSkeletonProps;

/**
 * Themed skeleton primitive. Renders a single grey shimmer block sized by the
 * `width` / `height` / `circle` / `count` props. Drop it directly into the
 * component's normal layout in place of the data-bound element it shadows.
 */
export const Skeleton: React.FC<SkeletonProps> = (props) => {
  const palette = React.useContext(SkeletonPalette);
  return (
    <RLSSkeleton
      baseColor={palette.baseColor}
      highlightColor={palette.highlightColor}
      {...props}
    />
  );
};

/** Props accepted by `<SkeletonGroup>`. */
export interface SkeletonGroupProps {
  /** Children rendered inside the themed scope. */
  children: React.ReactNode;
  /** Override the base shimmer colour. Defaults to the active theme. */
  baseColor?: string;
  /** Override the highlight shimmer colour. Defaults to the active theme. */
  highlightColor?: string;
}

/**
 * Theme provider - wrap a loading subtree so every nested `<Skeleton>` shares
 * the same colour palette without each consumer repeating the colour props.
 */
export const SkeletonGroup: React.FC<SkeletonGroupProps> = ({
  children,
  baseColor,
  highlightColor,
}) => {
  const inherited = React.useContext(SkeletonPalette);
  const palette = {
    baseColor: baseColor ?? inherited.baseColor,
    highlightColor: highlightColor ?? inherited.highlightColor,
  };
  return (
    <SkeletonPalette.Provider value={palette}>
      <SkeletonTheme {...palette}>{children}</SkeletonTheme>
    </SkeletonPalette.Provider>
  );
};
